import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {defaultAlert} from "../../utils/alert";
import {signup, MemberDto, handleTokenResponse} from "../../api/authApi";
import apiClient from "../../api/apiClient";

const SetPaymentPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');

    const [step, setStep] = useState<1 | 2>(1);
    const [firstPassword, setFirstPassword] = useState('');
    const [secondPassword, setSecondPassword] = useState('');
    const [numbers, setNumbers] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 0]);
    const [isLoading, setIsLoading] = useState(false);

    const currentPassword = step === 1 ? firstPassword : secondPassword;

    // 비밀번호 입력 처리
    const handleNumberClick = (num: number) => {
        if (isLoading) return;

        const password = step === 1 ? firstPassword : secondPassword;

        if (password.length < 6) {
            const newPassword = password + num;

            if (step === 1) {
                setFirstPassword(newPassword);

                if (newPassword.length === 6) {
                    setTimeout(() => {
                        setStep(2);
                    }, 300);
                }
            } else {
                setSecondPassword(newPassword);

                if (newPassword.length === 6) {
                    handleSubmit(newPassword);
                }
            }
        }
    };

    const handleDelete = () => {
        if (isLoading) return;

        if (step === 1) {
            setFirstPassword(firstPassword.slice(0, -1));
        } else {
            setSecondPassword(secondPassword.slice(0, -1));
        }
    };

    const handleShuffle = () => {
        if (isLoading) return;
        const shuffled = [...numbers].sort(() => Math.random() - 0.5);
        setNumbers(shuffled);
    };

    // 비밀번호 일치 검증 및 완료
    const handleSubmit = async (pwd: string) => {
        setIsLoading(true);

        try {
            // 비밀번호 일치 여부 확인
            if (firstPassword !== pwd) {
                defaultAlert('비밀번호가 일치하지 않습니다. 다시 시도해주세요.');
                setFirstPassword('');
                setSecondPassword('');
                setStep(1);
                setIsLoading(false);
                return;
            }

            // 회원가입 모드인 경우
            if (mode === 'signup') {
                const signupDataStr = sessionStorage.getItem('signupData');

                if (!signupDataStr) {
                    defaultAlert('회원가입 정보를 찾을 수 없습니다.');
                    navigate('/signup');
                    return;
                }

                const signupData = JSON.parse(signupDataStr) as MemberDto;

                // signup API 함수 사용
                const response = await signup({
                    ...signupData,
                    paymentPassword: firstPassword
                });

                handleTokenResponse(response.data);
                sessionStorage.removeItem('signupData');

                defaultAlert('회원가입이 완료되었습니다.');
                navigate('/', { replace: true });
            }
            // 비밀번호 변경 모드인 경우
            else if (mode === 'modify') {
                await apiClient.put('/api/front/member/payment-password', {
                    paymentPassword: firstPassword
                });

                defaultAlert('비밀번호가 변경되었습니다.');
                navigate('/mypage', { replace: true });
            }
            else {
                navigate(-1);
            }

        } catch (error) {
            console.error('Payment password setting failed:', error);

            if (mode === 'signup') {
                defaultAlert('회원가입에 실패했습니다.');
                sessionStorage.removeItem('signupData');
                navigate('/signup', { replace: true });
            } else {
                defaultAlert('처리에 실패했습니다.');
            }

            setFirstPassword('');
            setSecondPassword('');
            setStep(1);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (step === 2) {
            setSecondPassword('');
            setStep(1);
        } else {
            if (mode === 'signup') {
                sessionStorage.removeItem('signupData');
                navigate('/signup');
            } else {
                navigate(-1);
            }
        }
    };

    const passwordIndicators = Array.from({ length: 6 }, (_, i) => i);

    return (
        <>
            <header className="sub-header">
                <div className="header-inner">
                    <button
                        type="button"
                        className="closeBtn"
                        onClick={handleBack}
                        disabled={isLoading}
                    >
                        <span className="blind">닫기</span>
                    </button>
                </div>
            </header>

            <main className="container no-bottom-nav">
                <section>
                    <div className="passwordForm">
                        <h1>
                            {mode === 'signup' ? '결제비밀번호 설정' : '결제비밀번호 변경'}
                        </h1>
                        <h2>
                            {step === 1
                                ? '결제비밀번호 6자리를 입력해 주세요.'
                                : '확인을 위해 다시 한번 입력해 주세요.'}
                        </h2>
                        <div className="password-input inlinePassword-input">
                            <div className="password-indicator">
                                {passwordIndicators.map((i) => (
                                    <span
                                        key={i}
                                        className={`passCircle ${i < currentPassword.length ? 'filled' : ''}`}
                                        id={`passwd${i}`}
                                    />
                                ))}
                            </div>
                        </div>
                        {isLoading && <p className="loading-text">처리 중...</p>}
                    </div>
                </section>

                <section>
                    <div className="fixedBottom">
                        <ul className="keyboardF">
                            {numbers.slice(0, 9).map((num) => (
                                <li key={num}>
                                    <button
                                        type="button"
                                        className="key-btn"
                                        onClick={() => handleNumberClick(num)}
                                        disabled={isLoading}
                                    >
                                        {num}
                                    </button>
                                </li>
                            ))}
                            <li>
                                <button
                                    type="button"
                                    className="key-btn txt"
                                    onClick={handleShuffle}
                                    disabled={isLoading}
                                >
                                    재배열
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="key-btn"
                                    onClick={() => handleNumberClick(numbers[9])}
                                    disabled={isLoading}
                                >
                                    {numbers[9]}
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className="key-btn txt"
                                    onClick={handleDelete}
                                    disabled={isLoading}
                                >
                                    삭제
                                </button>
                            </li>
                        </ul>
                    </div>
                </section>
            </main>
        </>
    );
};

export default SetPaymentPasswordPage;